import React, { useState } from "react";
import { Globe, ChevronDown, ChevronUp, Info } from "lucide-react";
import { getFilterColor } from "../utils/filters";

const ContentCard = ({ 
  item, 
  contentTypeFilters = [], 
  themeFilters = [], 
  viewMode = 'grid', 
  isDarkMode, 
  onClick 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const getFaviconUrl = (url) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
    } catch (e) {
      return null;
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength).trim() + '...';
  };

  const truncateTitle = text => truncateText(text, 80);
  const truncateDescription = text => truncateText(text, 150);

  const getDisplayImage = () => {
    if (item.images && item.images.length > 0) {
      return item.images[item.mainImageIndex || 0].url;
    }
    if (item.metadata?.isYouTube && item.metadata?.videoId) {
      return `https://img.youtube.com/vi/${item.metadata.videoId}/maxresdefault.jpg`;
    }
    return item.image || item.metadata?.thumbnail || null;
  };

  const getThemeColor = (theme) => {
    switch (theme) {
      case "Creatie":
        return "bg-[#e8c2d9]";
      case "Development":
        return "bg-[#3cdc96]";
      case "Project management":
        return "bg-[#82e6e6]";
      default:
        return "bg-gray-500";
    }
  };
  
  const MetadataIcons = () => {
    if (!item) return null;

    const contentTypeFilter = contentTypeFilters?.find?.(f => f.type === item.contentType);
    const themeFilter = themeFilters?.find?.(f => f.type === item.theme);

    return (
      <div className="absolute top-3 right-3 flex gap-2 z-20">
        {contentTypeFilter?.Icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            item.theme ? getThemeColor(item.theme) : getFilterColor(contentTypeFilters, item.contentType)
          }`}>
            <contentTypeFilter.Icon 
              size={16} 
              className={item.theme === "Creatie" ? "text-[#282828]" : "text-white"} 
            />
          </div>
        )}
        {themeFilter?.Icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getThemeColor(item.theme)}`}>
            <themeFilter.Icon 
              size={16} 
              className={item.theme === "Creatie" ? "text-[#282828]" : "text-white"} 
            />
          </div>
        )}
      </div>
    );
  };

  const ImageSection = () => {
    const imageUrl = getDisplayImage();
    const isYouTube = item.metadata?.isYouTube || item.url?.includes('youtube.com') || item.url?.includes('youtu.be');
    
    return (
      <div 
        className="relative h-48 bg-gray-900 flex items-center justify-center overflow-hidden md:cursor-default cursor-pointer"
        onClick={(e) => {
          if (window.innerWidth < 768) {
            e.stopPropagation();
            onClick?.(item);
          }
        }}
      >
        <MetadataIcons />
        
        {imageUrl ? (
          <>
            <div 
              className="absolute inset-0 opacity-50"
              style={{
                backgroundImage: `url(${imageUrl})`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                filter: 'blur(8px)',
                transform: 'scale(1.1)'
              }}
            />
            <div className="relative w-full h-full z-10">
              <img
                src={imageUrl}
                alt={item.title}
                className="w-full h-full object-contain"
                onError={(e) => {
                  if (isYouTube && e.target.src.includes('maxresdefault')) {
                    const videoId = item.metadata?.videoId;
                    if (videoId) {
                      e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                      return;
                    }
                  }
                  
                  e.target.style.display = 'none';
                  const container = e.target.parentNode;
                  const faviconUrl = getFaviconUrl(item.url);
                  if (faviconUrl) {
                    container.innerHTML = `
                      <div class="flex flex-col items-center justify-center h-full space-y-2">
                        <img src="${faviconUrl}" class="w-6 h-6" />
                        <div class="text-sm text-gray-400">${new URL(item.url).hostname.replace('www.', '')}</div>
                      </div>
                    `;
                  } else {
                    container.innerHTML = '<div class="flex items-center justify-center h-full"><div class="text-gray-400"><Globe size={24} /></div></div>';
                  }
                }}
              />
              {isYouTube && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-600 bg-opacity-90">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <img 
              src={getFaviconUrl(item.url)} 
              className="w-6 h-6"
              alt=""
              onError={(e) => {
                e.target.style.display = 'none';
                const iconWrapper = document.createElement('div');
                iconWrapper.innerHTML = '<div class="text-gray-400"><Globe size={24} /></div>';
                e.target.parentNode.appendChild(iconWrapper);
              }}
            />
            <div className="text-sm text-gray-400">
              {item.url ? new URL(item.url).hostname.replace('www.', '') : ''}
            </div>
          </div>
        )}
      </div>
    );
  };

  const ActionButtons = () => (
    <div className="flex flex-col gap-3">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(item);
        }}
        className="w-full py-2.5 rounded-lg flex items-center justify-center gap-2 bg-[#e8c2d9] text-gray-900 hover:bg-opacity-90"
      >
        <Info size={16} />
        <span>Meer details</span>
      </button>
      {item.url && (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-full py-2.5 rounded-lg flex items-center justify-center gap-2 ${
            isDarkMode 
              ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={e => e.stopPropagation()}
        >
          <Globe size={16} />
          <span>Bron bekijken</span>
        </a>
      )}
    </div>
  );

  if (typeof window !== 'undefined' && window.innerWidth < 768) {
    return (
      <div className={`${isDarkMode ? 'bg-[#282828]' : 'bg-white'} rounded-lg shadow-lg`}>
        <ImageSection />
        
        <div className="p-4">
          {item.contributor && (
            <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {item.contributor}
            </p>
          )}
          
          <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {item.title}
          </h3>

          <div className={`mt-3 ${isExpanded ? 'pb-4' : ''}`}>
            <div className={`overflow-hidden transition-all duration-300 ${
              isExpanded ? 'max-h-[10000px]' : 'max-h-16'
            }`}>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {item.description}
              </p>

              {isExpanded && (
                <div className="mt-6">
                  <ActionButtons />
                </div>
              )}
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className={`w-full mt-3 pt-3 flex items-center justify-center gap-1 text-sm border-t ${
                isDarkMode 
                  ? 'border-gray-700 text-gray-400' 
                  : 'border-gray-200 text-gray-500'
              }`}
            >
              <span>{isExpanded ? 'Inklappen' : 'Lees meer'}</span>
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div 
        onClick={() => onClick?.(item)}
        className={`
          cursor-pointer transition-all duration-200
          ${isDarkMode ? 'bg-[#282828] hover:bg-[#383838]' : 'bg-white hover:bg-gray-50'}
          rounded-lg shadow-lg overflow-hidden
        `}
      >
        <div className="flex">
          <div className="w-48 flex-shrink-0 relative">
            <ImageSection />
          </div>
          <div className={`flex-1 p-4 ${isDarkMode ? 'bg-[#323232]' : ''}`}>
            {item.contributor && (
              <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {item.contributor}
              </p>
            )}
            <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              {truncateTitle(item.title)}
            </h3>
            <p className={`text-sm mt-2 line-clamp-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {truncateDescription(item.description)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={() => onClick?.(item)}
      className={`
        cursor-pointer transition-all duration-200
        ${isDarkMode ? 'bg-[#282828] hover:bg-[#383838]' : 'bg-white hover:bg-gray-50'}
        rounded-lg shadow-lg overflow-hidden
      `}
    >
      <ImageSection />
      <div className={`p-4 ${isDarkMode ? 'bg-[#323232]' : ''}`}>
        {item.contributor && (
          <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {item.contributor}
          </p>
        )}
        <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          {truncateTitle(item.title)}
        </h3>
        <p className={`text-sm line-clamp-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {truncateDescription(item.description)}
        </p>
      </div>
    </div>
  );
};

export default ContentCard;